import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Pilcrow
} from 'lucide-react';
import { toast } from 'sonner';
import { useGetWorkspaceQuery, useUpdateWorkspaceMutation } from '@/services/workspaces/queries';
import { useAuthStore } from '@/store/auth';

export default function PrivacyPolicyEditor() {
  const [isEdited, setIsEdited] = useState(false);

  const workspaceId = useAuthStore((state) => state.activeWorkspace) || '';

  // Fetch current policy
  const { data, isLoading }: any = useGetWorkspaceQuery(workspaceId, {
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Update mutation
  const { mutate: updatePolicy, isPending: isUpdating } = useUpdateWorkspaceMutation(workspaceId, () => {
    toast.success('Privacy policy updated successfully!');
  }, (error: any) => {
    toast.error('Failed to update policy');
    console.error(error);
  });

  // Editor setup
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:underline'
        }
      }),
      Underline
    ],
    content: data?.privacyPolicy || '',
    editable: true,
    onUpdate: ({ editor }) => {
      setIsEdited(editor.getHTML() !== data?.privacyPolicy);
    }
  });

  // Initialize editor
  useEffect(() => {
    if (data?.data?.privacyPolicy && editor) {
      editor.commands.setContent(data.data.privacyPolicy);
    }
  }, [data, editor]);

  const handleSave = async () => {
    if (!editor || !isEdited) return;
    updatePolicy({
      privacyPolicy: editor.getHTML(),
    });
    setIsEdited(false);
  };

  if (isLoading || !editor) {
    return (
      <div className='flex min-h-[300px] items-center justify-center rounded-md border bg-gray-50'>
        <p>Loading editor...</p>
      </div>
    );
  }

  // Get current heading level for display
  const getCurrentHeading = () => {
    if (editor.isActive('heading', { level: 1 })) return 'h1';
    if (editor.isActive('heading', { level: 2 })) return 'h2';
    if (editor.isActive('heading', { level: 3 })) return 'h3';
    return 'paragraph';
  };

  const renderHeadingIcon = () => {
    switch (getCurrentHeading()) {
      case 'h1':
        return <Heading1 className='h-3 w-3' />;
      case 'h2':
        return <Heading2 className='h-3 w-3' />;
      case 'h3':
        return <Heading3 className='h-3 w-3' />;
      default:
        return <Pilcrow className='h-3 w-3' />;
    }
  };

  return (
    <div className='space-y-4'>
      <div className='rounded-md border'>
        {/* Toolbar */}
        <div className='flex flex-wrap items-center gap-1 border-b bg-gray-50 p-2'>
          <div className='relative'>
            <select
              className='h-8 appearance-none rounded border bg-white pl-2 pr-6 text-sm'
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'paragraph') {
                  editor.chain().focus().setParagraph().run();
                } else if (value === 'h1') {
                  editor.chain().focus().toggleHeading({ level: 1 }).run();
                } else if (value === 'h2') {
                  editor.chain().focus().toggleHeading({ level: 2 }).run();
                } else if (value === 'h3') {
                  editor.chain().focus().toggleHeading({ level: 3 }).run();
                }
              }}
              value={getCurrentHeading()}
            >
              <option value='paragraph'>Normal</option>
              <option value='h1'>Heading 1</option>
              <option value='h2'>Heading 2</option>
              <option value='h3'>Heading 3</option>
            </select>
            <div className='pointer-events-none absolute right-1 top-1/2 flex -translate-y-1/2 items-center'>
              {renderHeadingIcon()}
            </div>
          </div>

          <Button
            variant='ghost'
            size='icon'
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'bg-gray-200' : ''}
            aria-label='Bold'
          >
            <Bold className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'bg-gray-200' : ''}
            aria-label='Italic'
          >
            <Italic className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'bg-gray-200' : ''}
            aria-label='Underline'
          >
            <UnderlineIcon className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            onClick={() => {
              const previousUrl = editor.getAttributes('link').href;
              const url = window.prompt('URL', previousUrl);
              if (url === null) return;
              if (url === '') {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange('link')
                  .unsetLink()
                  .run();
                return;
              }
              editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url })
                .run();
            }}
            className={editor.isActive('link') ? 'bg-gray-200' : ''}
            aria-label='Link'
          >
            <LinkIcon className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              editor.chain().focus().toggleBulletList().run();
            }}
            className={editor.isActive('bulletList') ? 'bg-gray-200' : ''}
            aria-label='Bullet List'
          >
            <List className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              editor.chain().focus().toggleOrderedList().run();
            }}
            className={editor.isActive('orderedList') ? 'bg-gray-200' : ''}
            aria-label='Ordered List'
          >
            <ListOrdered className='h-4 w-4' />
          </Button>
        </div>

        {/* Editor Content */}
        <EditorContent 
          editor={editor} 
          className='prose prose-sm max-w-none min-h-[300px] p-4 focus:outline-none [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6' 
        />
      </div>

      {/* Action Buttons */}
      <div className='flex justify-end'>
        {/* <Button variant='outline' onClick={() => navigate('/settings')}>
          ← Back to Settings
        </Button> */}
        <div className='space-x-4'>
          <Button
            variant='outline'
            onClick={() =>
              editor.commands.setContent(data?.data?.privacyPolicy || '')
            }
            disabled={!isEdited}
          >
            Reset
          </Button>
          <Button onClick={handleSave} disabled={!isEdited || isUpdating}>
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Edit Indicator */}
      {isEdited && (
        <p className='text-sm text-yellow-600'>You have unsaved changes</p>
      )}
    </div>
  );
}
